import axios, { AxiosInstance, AxiosProgressEvent, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

// Configuration de base de l'API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Interface pour étendre AxiosRequestConfig avec _retry
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Interface pour les réponses paginées
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Interface pour les réponses d'erreur standard
export interface ApiError {
  error: string;
  details?: string;
  fields?: Array<{
    field: string;
    message: string;
  }>;
  timestamp?: string;
  path?: string;
  method?: string;
}

// Configuration des timeouts et retry
const DEFAULT_TIMEOUT = 10000;
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000;

class ApiClient {
  private axiosInstance: AxiosInstance;
  private token: string | null = null;
  private onAuthFailure?: () => void; // Callback for 401 errors

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: DEFAULT_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Consolidated interceptors setup
   */
  private setupInterceptors(): void {
    // Request interceptor - Add token
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        
        if (import.meta.env.DEV) {
          console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        }
        
        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle auth errors and general responses
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (import.meta.env.DEV) {
          console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;
          
          const currentPath = window.location.pathname;
          
          // Don't logout if we're already on login page or during auth process
          if (currentPath !== '/login' && !currentPath.startsWith('/auth')) {
            console.log('Token expired or invalid, triggering logout...');
            
            // Clear token immediately
            this.clearToken();
            
            // Use setTimeout to avoid state update during render
            setTimeout(() => {
              if (this.onAuthFailure) {
                this.onAuthFailure();
              } else {
                // Fallback redirect if no callback is set
                window.location.href = '/login';
              }
            }, 0);
          }
        }

        // Handle other errors
        if (import.meta.env.DEV) {
          console.error('❌ API Error:', {
            status: error.response?.status,
            url: error.config?.url,
            message: error.message
          });
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Set the callback function to handle auth failures
   */
  setOnAuthFailure(callback: () => void): void {
    this.onAuthFailure = callback;
  }

  /**
   * Définir le token d'authentification
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Supprimer le token d'authentification
   */
  clearToken(): void {
    this.token = null;
  }

  /**
   * Requête GET
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.get(url, config);
    return response.data;
  }

  /**
   * Requête POST
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.post(url, data, config);
    return response.data;
  }

  /**
   * Requête PUT
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.put(url, data, config);
    return response.data;
  }

  /**
   * Requête PATCH
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.patch(url, data, config);
    return response.data;
  }

  /**
   * Requête DELETE
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance.delete(url, config);
    return response.data;
  }

  /**
   * Upload de fichier
   */
  async uploadFile<T = any>(
    url: string, 
    file: File, 
    onProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    };

    const response: AxiosResponse<T> = await this.axiosInstance.post(url, formData, config);
    return response.data;
  }

  /**
   * Requête avec retry automatique
   */
  async retryRequest<T = any>(
    requestFn: () => Promise<T>,
    attempts: number = RETRY_ATTEMPTS,
    delay: number = RETRY_DELAY
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      if (attempts > 1) {
        console.log(`🔄 Retrying request... (${RETRY_ATTEMPTS - attempts + 1}/${RETRY_ATTEMPTS})`);
        await this.delay(delay);
        return this.retryRequest(requestFn, attempts - 1, delay * 2);
      }
      throw error;
    }
  }

  /**
   * Utilitaire pour créer un délai
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Construire une URL avec des paramètres de requête
   */
  buildUrl(baseUrl: string, params: Record<string, any>): string {
    const url = new URL(baseUrl, API_BASE_URL);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, String(value));
      }
    });
    
    return url.pathname + url.search;
  }

  /**
   * Vérifier le statut de l'API
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.get('/health');
  }

  /**
   * Obtenir l'instance Axios brute (à utiliser avec parcimonie)
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Instance singleton du client API
export const apiClient = new ApiClient();

// Export des types pour utilisation dans d'autres fichiers
export type { AxiosRequestConfig, AxiosResponse };

// Utilitaires pour gérer les erreurs API
export const isApiError = (error: any): error is ApiError => {
  return error && typeof error.error === 'string';
};

export const getErrorMessage = (error: any): string => {
  if (isApiError(error)) {
    return error.details || error.error;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'Une erreur inconnue s\'est produite';
};

// Configuration pour les environnements
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  TIMEOUT: DEFAULT_TIMEOUT,
  RETRY_ATTEMPTS,
  RETRY_DELAY,
} as const;

export default apiClient;